// ============================================
// Creator Editor - Hashtags (ES6)
// 해시태그 입력, 검색, 자동완성
// ============================================

import { escapeHtml, showToast } from '../../utils.js';
import { HOME_ENDPOINTS } from '../../shared/api-endpoints.js';

// ============================================
// 상태
// ============================================

let currentHashtags = [];
let selectedSuggestionIndex = -1;
let searchDebounceTimer = null;

const MAX_HASHTAGS = 10;
const MAX_TAG_LENGTH = 30;
const DEBOUNCE_DELAY = 300;

// ============================================
// 초기화
// ============================================

export function initHashtags() {
  currentHashtags = [];
  selectedSuggestionIndex = -1;
  renderHashtagTags();
  updateHashtagCount();
  hideSuggestions();
}

// ============================================
// 태그 추가/제거
// ============================================

/**
 * 해시태그 추가
 * @param {string} tag - 태그 이름 (# 있어도 없어도 됨)
 * @returns {boolean} 성공 여부
 */
export function addHashtag(tag) {
  // 정규화: # 제거, 공백 제거, 소문자 변환 안함 (원본 유지)
  const normalizedTag = tag.replace(/^#/, '').trim();

  if (!normalizedTag) {
    return false;
  }

  if (normalizedTag.length > MAX_TAG_LENGTH) {
    showToast(`태그는 ${MAX_TAG_LENGTH}자 이하로 입력해 주세요`, 'error');
    return false;
  }

  // 중복 검사 (대소문자 무시)
  const lowerTag = normalizedTag.toLowerCase();
  if (currentHashtags.some(t => t.toLowerCase() === lowerTag)) {
    showToast('이미 추가된 태그예요', 'error');
    return false;
  }

  // 최대 개수 검사
  if (currentHashtags.length >= MAX_HASHTAGS) {
    showToast(`태그는 최대 ${MAX_HASHTAGS}개까지 추가할 수 있어요`, 'error');
    return false;
  }

  currentHashtags.push(normalizedTag);
  renderHashtagTags();
  updateHashtagCount();
  updateHiddenInput();
  hideSuggestions();

  return true;
}

/**
 * 해시태그 제거
 * @param {number} index - 제거할 태그 인덱스
 */
export function removeHashtag(index) {
  if (index < 0 || index >= currentHashtags.length) return;

  currentHashtags.splice(index, 1);
  renderHashtagTags();
  updateHashtagCount();
  updateHiddenInput();
}

/**
 * 현재 태그 목록 반환
 */
export function getHashtags() {
  return [...currentHashtags];
}

/**
 * 태그 목록 설정 (폼 로드 시)
 * @param {string[]} tags - 태그 배열
 */
export function setHashtags(tags) {
  currentHashtags = (tags || [])
    .map(t => t.replace(/^#/, '').trim())
    .filter(t => t.length > 0)
    .slice(0, MAX_HASHTAGS);

  renderHashtagTags();
  updateHashtagCount();
  updateHiddenInput();
}

// ============================================
// 렌더링
// ============================================

/**
 * 태그 칩 렌더링
 */
function renderHashtagTags() {
  const container = document.getElementById('hashtagTags');
  if (!container) return;

  if (currentHashtags.length === 0) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = currentHashtags.map((tag, index) => `
    <span class="hashtag-tag">
      <span class="hashtag-tag-text">#${escapeHtml(tag)}</span>
      <span class="hashtag-tag-remove" data-action="removeHashtag" data-index="${index}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </span>
    </span>
  `).join('');
}

/**
 * 카운트 배지 업데이트
 */
function updateHashtagCount() {
  const countEl = document.getElementById('hashtagCount');
  if (countEl) {
    countEl.textContent = currentHashtags.length;
  }
}

/**
 * hidden input 업데이트 (저장용)
 */
function updateHiddenInput() {
  const hiddenInput = document.getElementById('editorHashtags');
  if (hiddenInput) {
    // #을 붙여서 저장 (기존 방식과 호환)
    hiddenInput.value = currentHashtags.map(t => `#${t}`).join(' ');
  }
}

// ============================================
// 자동완성 검색
// ============================================

/**
 * 태그 검색 (디바운스 적용)
 * @param {string} query - 검색어
 */
export function searchTags(query) {
  // 디바운스 클리어
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }

  const normalizedQuery = query.replace(/^#/, '').trim();

  if (!normalizedQuery) {
    hideSuggestions();
    return;
  }

  if (normalizedQuery.length < 1) {
    hideSuggestions();
    return;
  }

  searchDebounceTimer = setTimeout(async () => {
    try {
      const response = await fetch(
        `${HOME_ENDPOINTS.searchTags}?q=${encodeURIComponent(normalizedQuery)}&limit=8`
      );

      if (!response.ok) {
        throw new Error('검색 실패');
      }

      const result = await response.json();

      if (result.success) {
        renderSuggestions(result.tags || [], normalizedQuery);
      } else {
        renderSuggestions([], normalizedQuery);
      }
    } catch (e) {
      console.warn('[Hashtags] 태그 검색 실패:', e);
      // 검색 실패해도 새 태그 만들기 옵션은 표시
      renderSuggestions([], normalizedQuery);
    }
  }, DEBOUNCE_DELAY);
}

/**
 * 자동완성 드롭다운 렌더링
 * @param {Array} tags - 검색 결과
 * @param {string} query - 검색어
 */
function renderSuggestions(tags, query) {
  const container = document.getElementById('hashtagSuggestions');
  if (!container) return;

  selectedSuggestionIndex = -1;

  // 이미 추가된 태그 필터링
  const filteredTags = tags.filter(
    t => !currentHashtags.some(ct => ct.toLowerCase() === t.name.toLowerCase())
  );

  // 검색어가 기존 태그와 정확히 일치하는지 확인
  const exactMatch = tags.some(t => t.name.toLowerCase() === query.toLowerCase());
  const alreadyAdded = currentHashtags.some(t => t.toLowerCase() === query.toLowerCase());

  let html = '';

  // 검색 결과
  if (filteredTags.length > 0) {
    html += filteredTags.map((tag, index) => `
      <div class="suggestion-item" data-tag="${escapeHtml(tag.name)}" data-index="${index}">
        <span class="tag-name">#${escapeHtml(tag.name)}</span>
        <span class="tag-count">${formatCount(tag.usageCount || 0)}</span>
      </div>
    `).join('');
  }

  // 새 태그 만들기 옵션 (정확히 일치하지 않고 아직 추가 안된 경우)
  if (!exactMatch && !alreadyAdded && query.length > 0) {
    html += `
      <div class="suggestion-item new-tag" data-tag="${escapeHtml(query)}" data-index="${filteredTags.length}">
        <span class="tag-name">"${escapeHtml(query)}" 태그 만들기</span>
        <span class="tag-badge">NEW</span>
      </div>
    `;
  }

  // 결과 없음
  if (!html) {
    if (alreadyAdded) {
      html = '<div class="suggestion-empty">이미 추가된 태그예요</div>';
    } else {
      html = '<div class="suggestion-empty">검색 결과 없음</div>';
    }
  }

  container.innerHTML = html;
  container.classList.remove('hidden');
}

/**
 * 드롭다운 숨기기
 */
export function hideSuggestions() {
  const container = document.getElementById('hashtagSuggestions');
  if (container) {
    container.classList.add('hidden');
    container.innerHTML = '';
  }
  selectedSuggestionIndex = -1;
}

/**
 * 키보드 네비게이션
 * @param {string} direction - 'up' | 'down'
 */
export function navigateSuggestions(direction) {
  const container = document.getElementById('hashtagSuggestions');
  if (!container || container.classList.contains('hidden')) return;

  const items = container.querySelectorAll('.suggestion-item');
  if (items.length === 0) return;

  // 현재 선택 해제
  items.forEach(item => item.classList.remove('selected'));

  if (direction === 'down') {
    selectedSuggestionIndex = (selectedSuggestionIndex + 1) % items.length;
  } else {
    selectedSuggestionIndex = selectedSuggestionIndex <= 0
      ? items.length - 1
      : selectedSuggestionIndex - 1;
  }

  items[selectedSuggestionIndex]?.classList.add('selected');
  items[selectedSuggestionIndex]?.scrollIntoView({ block: 'nearest' });
}

/**
 * 현재 선택된 항목 추가
 * @returns {boolean} 성공 여부
 */
export function selectCurrentSuggestion() {
  const container = document.getElementById('hashtagSuggestions');
  if (!container || container.classList.contains('hidden')) return false;

  const items = container.querySelectorAll('.suggestion-item');

  // 선택된 항목이 있으면 그것 사용
  if (selectedSuggestionIndex >= 0 && items[selectedSuggestionIndex]) {
    const tag = items[selectedSuggestionIndex].dataset.tag;
    if (tag) {
      return addHashtag(tag);
    }
  }

  // 선택된 항목 없으면 첫 번째 항목 사용
  if (items.length > 0) {
    const tag = items[0].dataset.tag;
    if (tag) {
      return addHashtag(tag);
    }
  }

  return false;
}

// ============================================
// 유틸리티
// ============================================

/**
 * 숫자 포맷 (1K, 1.2K 등)
 */
function formatCount(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
}
